// External.
import { useRef } from "react";

// NVIDIA.
import {
    Block,
    Grid,
    Icon,
    Result,
    Spinner,
    Tag,
    Text,
} from "@kaizen-ui/complete";

// Local.
import { DocumentationDatabase } from "../library/documentation-database";

import type { DocumentationIndex } from "../library/documentation-database";

import type { Documentation } from "../services/document-service";

// Url for where Omniverse documentation exists.
//const DOCUMENTATION_URL = window.location.pathname.substring(1);
//process.env.REACT_APP_DOCUMENTATION_URL ||
//"http://docs.omniverse.nvidia.com/kit/docs";

// Optional path prefix prepended to doc unit links (e.g. "../" when the
// portal is nested one level below the doc units).
const DOC_LINK_PREFIX = process.env.REACT_APP_DOC_LINK_PREFIX || "";

export interface DocumentListProperties {
    documents: Documentation[];
    isLoading?: boolean;
    filterText?: string;
}

// Helper function for rendering an individual documentation card.
function renderDocumentationCard(doc: Documentation): JSX.Element {
    // Render a linked block containing basic information about the documentation.
    // Use a link so that it's clickable and the url preview in-browser will
    // show the destination. This keeps it simple.
    return (
        <a
            href={`${DOC_LINK_PREFIX}${doc.ext}/`}
            // href={`${DOCUMENTATION_URL}/${doc.ext}/`}
            key={doc.ext}
            style={{ margin: "1em" }}
        >
            <Block title={doc.name}>
                <Grid columns={10} gutter={1}>
                    <Grid.Row>
                        <Grid.Column span={9}>
                            <div>{doc.description}</div>
                            <div style={{ paddingTop: "1em", width: "100%" }}>
                                {doc.keywords.map((keyword) => (
                                    <span
                                        style={{ marginRight: "0.25em" }}
                                        key={keyword}
                                    >
                                        <Tag
                                            variant="solid"
                                            clickable={false}
                                            color="gray"
                                        >
                                            {keyword}
                                        </Tag>
                                    </span>
                                ))}
                            </div>
                        </Grid.Column>
                        <Grid.Column span={1}>
                            <div style={{ margin: "-20px 0" }}>
                                <Icon
                                    name="ArrowRight"
                                    variant="solid"
                                    size={40}
                                    color="#76B900"
                                />
                            </div>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Block>
        </a>
    );
}

// Helper function for filtering documents based on search criteria.
function getFilteredDocs(
    props: DocumentListProperties,
    docDb?: DocumentationIndex
): Documentation[] {
    if (!docDb || props.documents.length === 0 || !props.filterText) {
        return props.documents;
    }

    const matchers = docDb.match(props.filterText);

    return matchers;
}

function renderEmptyDocuments(): JSX.Element {
    return <Result status="noResults" />;
}

// Helper function for main render path.
function renderDocuments(
    props: DocumentListProperties,
    docDb?: DocumentationIndex
): JSX.Element {
    const filtered = getFilteredDocs(props, docDb);

    if (filtered.length === 0) {
        return renderEmptyDocuments();
    }

    return (
        <div>
            <div>
                <Text>Showing {filtered.length} results</Text>
            </div>
            <div>{filtered.map((doc) => renderDocumentationCard(doc))}</div>
        </div>
    );
}

// Helper function for rendering loading spinner.
function renderIsLoading(): JSX.Element {
    return (
        <Block>
            <Spinner size="large" />
        </Block>
    );
}

// Main React handler function.
function DocumentList(props: DocumentListProperties): JSX.Element {
    const searchDbRef = useRef<DocumentationIndex>();

    if (!searchDbRef.current && props.documents.length > 0) {
        searchDbRef.current = DocumentationDatabase.build(props.documents);
    }

    if (props.isLoading) {
        return renderIsLoading();
    }

    return renderDocuments(props, searchDbRef.current);
}

export { DocumentList };
